import { NavLink } from "react-router-dom";

import { LastUpdatedLabel } from "../components/LastUpdatedLabel";
import { NumberedListCard, type NumberedListItem } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { SignalStrip } from "../components/SignalStrip";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type OverviewPageData = Awaited<ReturnType<typeof loadOverviewPage>>;

async function loadOverviewPage() {
  const [recommendation, overview, site, economics, risk, concept, demand, comps, fallback] = await Promise.all([
    api.getRecommendation(),
    api.getOverview(),
    api.getSite(),
    api.getEconomics(),
    api.getRisk(),
    api.getConcept(),
    api.getDemand(),
    api.getComps(),
    api.getFallback()
  ]);

  return { recommendation, overview, site, economics, risk, concept, demand, comps, fallback };
}

function uniqueItems(items: string[]): string[] {
  const seen = new Set<string>();

  return items
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) {
        return false;
      }

      const key = item.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function toSentence(text: string): string {
  const trimmed = text.trim();
  const firstSentence = trimmed.match(/.*?[.?!](?:\s|$)/);
  return firstSentence ? firstSentence[0].trim() : trimmed;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value);
}

function RationaleCard({
  eyebrow,
  title,
  points
}: {
  eyebrow: string;
  title: string;
  points: NumberedListItem[];
}) {
  return (
    <section
      style={{
        borderRadius: "24px",
        padding: "24px",
        background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
      }}
    >
      <div className="stack-md">
        <div className="stack-xs">
          <span className="card-eyebrow">{eyebrow}</span>
          <h3 className="card-title">{title}</h3>
        </div>
        <div style={{ display: "grid", gap: "14px" }}>
          {points.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "30px minmax(0, 1fr)",
                gap: "12px",
                alignItems: "start",
                paddingTop: index === 0 ? 0 : "14px",
                borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.06)"
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "30px",
                  height: "30px",
                  borderRadius: "999px",
                  background: "rgba(127, 208, 255, 0.14)",
                  border: "1px solid rgba(127, 208, 255, 0.32)",
                  color: "#7fd0ff",
                  fontSize: "0.8rem",
                  fontWeight: 700
                }}
              >
                {index + 1}
              </span>
              <div className="stack-xs">
                <div style={{ color: "#f4f8ff", lineHeight: 1.45 }}>{item.label}</div>
                {item.detail ? (
                  <div style={{ color: "rgba(221, 231, 247, 0.76)", lineHeight: 1.5 }}>{item.detail}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InvestorPathCard({
  eyebrow,
  title,
  body,
  href
}: {
  eyebrow: string;
  title: string;
  body: string;
  href?: string;
}) {
  const shellStyle = {
    display: "block",
    textDecoration: "none",
    color: "inherit",
    borderRadius: "24px",
    padding: "22px",
    background: "linear-gradient(180deg, rgba(11, 17, 30, 0.92), rgba(8, 12, 22, 0.8))",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 18px 40px rgba(3, 8, 20, 0.18)"
  } as const;

  const content = (
    <div className="stack-sm">
      <span className="card-eyebrow">{eyebrow}</span>
      <h3 className="card-title">{title}</h3>
      <p className="card-copy">{body}</p>
    </div>
  );

  if (!href) {
    return <section style={shellStyle}>{content}</section>;
  }

  return (
    <NavLink to={href} style={shellStyle}>
      {content}
    </NavLink>
  );
}

export function OverviewPage() {
  const { data, loading, error } = useContent("investment-case-page", loadOverviewPage);

  if (!data) {
    return (
      <PageContainer
        eyebrow="Investment Case"
        title="A clearer opening investor read."
        lede="The opening page now exists to state the opportunity, the lead concept, the main supports, the main gates, and the next investor path in one scan."
      >
        {loading ? <div className="loading-panel shimmer-card">Loading investment case...</div> : null}
        {error ? <div className="error-panel">{error}</div> : null}
      </PageContainer>
    );
  }

  const { recommendation, overview, site, economics, risk, concept, demand, comps, fallback } = data;
  const leadConcept = concept.conceptSummary.leadConcept;
  const totalGates =
    risk.riskSummary.remainingOpenRisks.length +
    economics.costSummary.openCostRisks.length +
    economics.operationsSummary.openOperatingItems.length;
  const utilityBaseline = formatCurrency(economics.costSummary.utilityBaseline.citySideKnownBaseline);
  const thesis =
    `400 Norwood now has a clearer ${leadConcept.concept.toLowerCase()} investment case: the parcel and zoning read are workable, market support is visible, and the remaining issues are a finite gate list rather than an open-ended diligence stack.`;
  const whyNowRead = uniqueItems([
    site.siteSummary.overallPosture,
    economics.costSummary.costPosture,
    risk.riskSummary.riskPosture
  ]).join(" · ");

  const supportSignals: NumberedListItem[] = [
    {
      label: toSentence(site.siteSummary.leadConceptFit),
      detail: `Asset fit: ${site.siteSummary.overallPosture}`
    },
    {
      label: toSentence(leadConcept.currentCompSupport || comps.compsSummary.currentCompRead),
      detail: `Commercial read: ${demand.currentDemandRead}`
    },
    {
      label: toSentence(economics.costSummary.leadConceptCostFit),
      detail: `Cost posture: ${economics.costSummary.costPosture}`
    },
    {
      label: toSentence(fallback.currentBestRead),
      detail: `Fallback stays alive: ${toSentence(fallback.longTermRentalRead)}`
    }
  ];

  const gateSignals = uniqueItems([
    ...recommendation.topCautions,
    ...risk.riskSummary.remainingOpenRisks,
    ...economics.costSummary.openCostRisks,
    ...economics.operationsSummary.openOperatingItems
  ])
    .slice(0, 4)
    .map((item) => ({ label: item }));

  const conceptRationale: NumberedListItem[] = [
    {
      label: leadConcept.corePositioning,
      detail: leadConcept.bestFitGuestThesis
    },
    {
      label: leadConcept.whyItLeads,
      detail: leadConcept.currentCompSupport
    },
    {
      label: `Fallback retained: ${overview.fallbackConcept}`,
      detail: toSentence(fallback.currentBestRead)
    }
  ];

  const assetRationale: NumberedListItem[] = [
    {
      label: toSentence(site.siteSummary.leadConceptFit),
      detail: `Parcel and zoning posture: ${site.siteSummary.overallPosture}`
    },
    {
      label: toSentence(demand.currentDemandRead),
      detail: `Comparable support: ${comps.compsSummary.currentCompRead}`
    },
    {
      label: `Known utility anchor: ${utilityBaseline}`,
      detail: toSentence(economics.costSummary.utilityBaseline.summary)
    }
  ];

  return (
    <PageContainer
      eyebrow="Investment Case"
      title="A clearer opening investor read."
      lede="This page now leads with the opportunity, the concept, the main supports, the main gates, and the next path into Financials and market proof."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading investment case...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}

      <div className="stack-xl">
        <PremiumHero
          eyebrow="Investment Thesis"
          title="A compact-home opportunity with a clearer go-forward path."
          subtitle={thesis}
          backgroundImage="/comps/104-f-st-streetview.jpg"
          backgroundLabel="Grounded local property context"
          badges={
            <>
              <SignalPill label={recommendation.label} tone="warning" />
              <SignalPill label={leadConcept.concept} tone="positive" />
              <SignalPill label={`${totalGates} live gates`} tone="neutral" />
            </>
          }
          actions={
            <>
              <NavLink to="/financials" className="link-button link-button-primary">
                Open Financials
              </NavLink>
              <NavLink to="/market-support" className="link-button link-button-secondary">
                Open Market Support
              </NavLink>
            </>
          }
          aside={
            <div className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">Why This / Why Now</span>
                <div style={{ fontSize: "1.6rem", lineHeight: 1.05, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                  {overview.leadConcept}
                </div>
                <p style={{ margin: 0, color: "rgba(223, 233, 248, 0.78)", lineHeight: 1.5 }}>
                  {toSentence(site.siteSummary.leadConceptFit)}
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gap: "12px"
                }}
              >
                {[
                  ["Why now", whyNowRead],
                  ["Commercial read", demand.currentDemandRead],
                  ["Fallback", fallback.currentBestRead]
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "18px",
                      padding: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)"
                    }}
                  >
                    <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                      {label}
                    </div>
                    <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.45 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div className="row-wrap gap-sm">
                <SignalPill label={recommendation.confidencePosture} tone="neutral" />
                <SignalPill label={risk.riskSummary.riskPosture} tone="warning" />
              </div>
              <LastUpdatedLabel value={overview.lastUpdated} />
            </div>
          }
        />

        <SignalStrip
          items={[
            {
              label: "Recommendation",
              value: recommendation.label,
              note: recommendation.confidencePosture,
              tone: "warning"
            },
            {
              label: "Lead concept",
              value: leadConcept.concept,
              note: leadConcept.bestFitGuestThesis,
              tone: "positive"
            },
            {
              label: "Asset fit",
              value: site.siteSummary.overallPosture,
              note: toSentence(site.siteSummary.leadConceptFit),
              tone: "positive"
            },
            {
              label: "Market support",
              value: comps.compsSummary.currentCompRead,
              note: demand.currentDemandRead,
              tone: "neutral"
            },
            {
              label: "Open gates",
              value: `${totalGates}`,
              note: risk.riskSummary.riskPosture,
              tone: "warning"
            }
          ]}
        />

        <div className="grid-two">
          <NumberedListCard
            eyebrow="Top Supports"
            title="Why the opportunity clears today"
            description="The current case is supported by a short set of concrete proof layers, not by a generic diligence stack."
            items={supportSignals}
            tone="positive"
          />
          <NumberedListCard
            eyebrow="Top Gates"
            title="What still needs to be watched"
            description="The remaining issues are visible and bounded. They still control conviction, but they no longer hide the main case."
            items={gateSignals}
            tone="warning"
          />
        </div>

        <section
          style={{
            borderRadius: "28px",
            padding: "24px",
            background: "linear-gradient(180deg, rgba(8, 13, 24, 0.94), rgba(7, 11, 20, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 50px rgba(3, 8, 20, 0.22)"
          }}
        >
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Why This Concept / Why This Asset</span>
              <h2 className="card-title">The concept fits the site, and the site still fits the market read.</h2>
              <p className="card-copy" style={{ maxWidth: "58ch" }}>
                This section keeps the thesis concrete: why the current concept leads, and why the parcel-market combination still supports it.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gap: "18px",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
              }}
            >
              <RationaleCard eyebrow="Why this concept" title={leadConcept.concept} points={conceptRationale} />
              <RationaleCard eyebrow="Why this asset" title="Parcel, market, and utility context" points={assetRationale} />
            </div>
          </div>
        </section>

        <section
          style={{
            borderRadius: "28px",
            padding: "24px",
            background: "linear-gradient(180deg, rgba(8, 13, 24, 0.94), rgba(7, 11, 20, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 50px rgba(3, 8, 20, 0.22)"
          }}
        >
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Next Investor Path</span>
              <h2 className="card-title">Go next where conviction still moves.</h2>
              <p className="card-copy" style={{ maxWidth: "58ch" }}>
                Financials and Market Support now carry the two follow-up reads that can still change the answer. Team and capital ask come next, but they are not being faked on this page.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gap: "16px",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))"
              }}
            >
              <InvestorPathCard
                eyebrow="Next up"
                title="Financials"
                body="Open the budget, operating model, underwriting, fallback, sensitivities, and disclosures in one governed surface."
                href="/financials"
              />
              <InvestorPathCard
                eyebrow="Then"
                title="Market Support"
                body="Pressure-test the commercial case through demand, comp realism, and local precedent."
                href="/market-support"
              />
              <InvestorPathCard
                eyebrow="Now live"
                title="Team & Ask"
                body="Open the execution-credibility and capital-boundary surface for the current public sponsor posture, use-of-funds basis, and financing disclosures."
                href="/team-ask"
              />
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
